import { Component, OnInit, ViewChild, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Auth } from 'aws-amplify';
import { Logger } from '../../util/logger/logger.service';
import { UserService } from '../../services/user.service';
import { IonInput, LoadingController, NavController, Platform } from '@ionic/angular';
import { DOCUMENT } from '@angular/common';
import { SegmentCMOSService } from '../../services/segment-cmos.service';
import { presentLoader } from 'src/app/util/ion-util';
import { PixelEventType, AppcastService } from 'src/app/services/appcast.service';
import { AppRoutingUtil } from 'src/app/util/app-routing-util';
import { IntercomService } from '../../services/intercom.service';
import { phoneValidationRegex, toE164 } from 'src/app/util/phone-util';
import { COMPROMISED_PASSWORD, EMAIL_ALREADY_EXISTS } from 'src/app/util/cognito-error-util';

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit {

    public error: any;

    form: UntypedFormGroup;

    showPassword = false;
    submitButtonDisabled = false;

    @ViewChild('passwordInput') 
    private passwordInput: IonInput;

    constructor(private formBuilder: UntypedFormBuilder,
                private loadingCtrl: LoadingController,
                private navCtrl: NavController,
                private segmentCMOS: SegmentCMOSService,
                private userService: UserService,
                private platform: Platform,
                private appcastService: AppcastService,
                private logger: Logger,
                private intercomService: IntercomService,
                @Inject(DOCUMENT) private document: Document
    ) {}

    ngOnInit() {
        this.form = this.formBuilder.group({
            //first_name: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(32), Validators.pattern(/^[A-Za-zÀ-ÖØ-öø-ÿ-.' ' ]*$/)])], // This line supports diacritics
            first_name: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(32), Validators.pattern(/^[A-Za-z-.'' ]*$/)])],
            last_name: ['', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(32), Validators.pattern(/^[A-Za-z-.'' ]*$/)])],
            zipcode: ['', Validators.compose([Validators.required, Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)])],
            email: ['', Validators.compose([Validators.required, Validators.email, Validators.pattern(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/)])],
            password: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(128)])],
            phone_number: ['', Validators.compose([Validators.required, Validators.pattern(phoneValidationRegex)])],
            referrerCode: ['', { validators: Validators.compose([Validators.maxLength(10)])}],
            policyCheckbox: [false, Validators.compose([Validators.required, Validators.requiredTrue])]
        });
    }

    $msg: string;
    async onSubmit() {
        // Prevent submit button from being double pressed by introducing a 300ms timeout
        this.submitButtonDisabled = true;
        setTimeout(() => {
            this.submitButtonDisabled = false;
          }, 300);

        // Trim whitespace from fields that allow whitespace
        this.form.controls.first_name.setValue(this.form.value.first_name.trim());
        this.form.controls.last_name.setValue(this.form.value.last_name.trim());
        this.form.controls.email.setValue(this.form.value.email.toLowerCase());
        this.form.markAllAsTouched();  // after a submit attempt we want to treat all fields like they've been 'touched'.

        if (!this.form.value.referrerCode) {
            delete this.form.value.referrerCode;
        }

        if (this.form.invalid) {
            this.$msg = 'All form fields are required, unless otherwise noted. Please fill out all form fields.';
            return this.$msg;
        }

        if (this.form.value.policyCheckbox === true) {
            delete this.form.value.policyCheckbox;
        }

        const loader = await presentLoader(this.loadingCtrl);

        // create user in Cognito
        try {
            await this.userService.registerCognito(this.form.value.email, this.form.value.password, toE164(this.form.value.phone_number));
        } catch (error) {
            await loader.dismiss();
            this.logger.error(error);
            if (EMAIL_ALREADY_EXISTS.toLowerCase() === error.message.toLowerCase()) {
                this.error = 'ER_DUP_EMAIL';
            } else if (COMPROMISED_PASSWORD.toLowerCase() === error.message.toLowerCase()) {
                this.error = 'ER_COMPROMISED_PASSWORD';
            } else {
                this.error = 'ERROR';
            }
            return;
        }

        // create user in api-v1
        this.userService.register(this.form.value)
            .subscribe({
                next: async () => {
                    await loader.dismiss();
                    if (!this.platform.is('hybrid')) {
                        this.segmentCMOS.addEvent(
                            'web_signup', {'cancelation_token': `websignup_${this.userService.currentUser.userInfo.externalId}`}
                        )
                    }
                    try {
                        // Send phone verification code through Cognito
                        Auth.verifyCurrentUserAttribute('phone_number');
                        this.appcastService.fireTrackingPixel(PixelEventType.NEW_USER_SIGN_UP, this.userService.currentUser.userInfo.externalId);
                    } catch (error) {
                        this.logger.error(error);
                        this.segmentCMOS.addEvent('registration_error', {
                            message: error.message
                        });
                    } finally {
                        this.navCtrl.navigateForward(AppRoutingUtil.ACCOUNT_VERIFICATION_CODE);
                    }
                },
                error: async error => {
                    await loader.dismiss();
                    this.logger.error(error);
                    this.error = error.error.reason;
                }
            });
    }

    toggleShowPassword() {
        this.showPassword = !this.showPassword;
        this.passwordInput.type = this.showPassword ? 'text' : 'password';
    }

    selectNext(event: KeyboardEvent) {
        const nodeList = this.document.querySelectorAll('.focus-field');
        for (let i = 0; i < nodeList.length; i++) {
            if (nodeList[i] === event.currentTarget) {
                const nextElement: IonInput = nodeList[i + 1] as unknown as IonInput;
                nextElement.setFocus();
            }
        }
    }

    onOpenIntercom() {
        this.intercomService.showNewMessage();
    }
}
